/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import { useForm } from '@folklore/forms';
import { faL } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuthLogout } from '../../hooks/useAuth';
import { CreateCommentRequest, useCreateComment } from '../../hooks/useComments';

import { useSettings } from '../../contexts/SiteContext';
import AvatarButton from '../buttons/AvatarButton';
import FormButton from '../buttons/FormButton';
import RoundedButton from '../buttons/RoundedButton';
import TextField from '../fields/TextField';
import TextareaField from '../fields/TextareaField';
import AccountIcon from '../icons/AccountIcon';
import FormTitle from '../typography/FormTitle';
import FormControl from './FormControl';
import IdentifyForm from './IdentifyForm';

import styles from '../../styles/forms/comment-form.module.css';

interface CommentFormProps {
    commentableKey: string | null;
    parent?: string | null;
    maxLength?: number | null;
    className?: string;
    onComplete?: (newComment: Comment, parentComment?: Comment | null) => void | null;
}

function CommentForm({
    commentableKey,
    parent = null,
    maxLength: providedMaxLength = null,
    className,
    onComplete = null,
}: CommentFormProps) {
    const { commentsMaxLength = 1500 } = useSettings();
    const maxLength = providedMaxLength || commentsMaxLength || 1500;
    const { createCommentAsync } = useCreateComment(commentableKey);
    const user = useUser();
    const { name = null, hasCommented = false } = user || {};
    const [nameEditable, setNameEditable] = useState(false);
    const [nameClicked, setNameClicked] = useState(false);
    const [formValue, setFormValue] = useState<{
        name?: string | null;
    }>({});
    const hasName = !isEmpty(name);
    const postForm = useCallback(
        (action, data) =>
            createCommentAsync({
                ...(parent !== null ? { parent } : {}),
                ...(data as CreateCommentRequest),
            }),
        [createCommentAsync, parent],
    );
    const onFormComplete = useCallback(
        ({ comment: newComment, parent: parentComment = null }) => {
            setFormValue(null);
            setNameEditable(false);
            setNameClicked(false);
            if (onComplete !== null) {
                onComplete(newComment, parentComment);
            }
        },
        [onComplete],
    );
    const { fields, onSubmit, setErrors } = useForm({
        fields: ['body', 'parent', 'name'],
        postForm,
        value: formValue,
        setValue: setFormValue,
        onComplete: onFormComplete,
    });

    // useEffect(() => {
    //     const { name: formName = null } = formValue || {};
    //     if (hasName && formName === null && !hasCommented) {
    //         setFormValue({
    //             ...formValue,
    //             name,
    //         });
    //     }
    // }, [hasName, name, formValue]);

    const nameFieldRef = useRef<HTMLInputElement>(null);
    const onClickEditName = useCallback(() => {
        const newNameEditable = !nameEditable;
        setNameEditable(newNameEditable);
        setNameClicked(true);
        const { name: formName, ...formWithoutName } = formValue || {};
        setFormValue(
            newNameEditable
                ? {
                      ...formWithoutName,
                      name,
                  }
                : formWithoutName,
        );
    }, [nameEditable, formValue, name]);
    const canEditName = user !== null && (!hasCommented || nameEditable || !hasName);

    const { logout } = useAuthLogout();

    const onClickLogout = useCallback(() => {
        logout();
        setNameEditable(false);
        setFormValue(null);
    }, [logout]);

    useEffect(() => {
        if (user !== null && (!hasName || !hasCommented) && !nameEditable) {
            setNameEditable(true);
            setFormValue({
                ...formValue,
                name,
            });
        }
    }, [user]);

    useEffect(() => {
        if (nameEditable && nameClicked) {
            nameFieldRef.current?.focus();
        }
    }, [nameEditable, nameClicked]);

    const { onChange: bodyOnChange } = fields.body;
    const onBodyChange = useCallback(
        (value: string | null, e) => {
            e.currentTarget.scrollTop = e.currentTarget.scrollHeight;
            bodyOnChange(value);
        },
        [bodyOnChange],
    );

    const [bodyFocused, setBodyFocused] = useState(false);
    const onBodyFocus = useCallback(() => {
        setBodyFocused(true);
    }, []);
    const onBodyBlur = useCallback(() => {
        setBodyFocused(false);
    }, []);

    return (
        <div className={classNames([styles.container, className])}>
            {user === null ? (
                <div className={styles.login}>
                    <FormTitle className={styles.title}>
                        Vous voulez commenter?
                        <br />
                        Identifiez-vous! <small>(c’est gratuit)</small>
                    </FormTitle>
                    <IdentifyForm
                        source={`comments:${commentableKey}`}
                        className={styles.loginForm}
                    />
                </div>
            ) : (
                <form className={styles.form} onSubmit={onSubmit}>
                    <AvatarButton className={styles.avatarButton} />
                    <FormControl
                        {...fields.name}
                        disabled={user === null}
                        className={classNames([
                            styles.nameFormControl,
                            {
                                [styles.editable]: nameEditable,
                            },
                        ])}
                    >
                        <div className={styles.nameFieldInner}>
                            <AccountIcon circleColor="transparent" className={styles.icon} />
                            {nameEditable ? (
                                <TextField
                                    {...(canEditName
                                        ? fields.name
                                        : {
                                              name: 'name',
                                              value: name,
                                          })}
                                    placeholder="Votre nom"
                                    ref={nameFieldRef}
                                    className={styles.nameField}
                                    disabled={!canEditName}
                                />
                            ) : (
                                <div className={styles.nameField}>{name}</div>
                            )}
                            {hasCommented && hasName ? (
                                <RoundedButton
                                    onClick={onClickEditName}
                                    className={classNames([styles.smallButton, styles.editButton])}
                                >
                                    {nameEditable ? 'Annuler' : 'Changer'}
                                </RoundedButton>
                            ) : null}
                            <RoundedButton
                                onClick={onClickLogout}
                                className={classNames([styles.smallButton, styles.logoutButton])}
                            >
                                Se déconnecter
                            </RoundedButton>
                        </div>
                    </FormControl>
                    <FormControl
                        {...fields.body}
                        disabled={user === null}
                        className={classNames([
                            styles.bodyFormControl,
                            {
                                [styles.focus]: bodyFocused,
                            },
                        ])}
                    >
                        <TextareaField
                            {...fields.body}
                            onChange={onBodyChange}
                            onFocus={onBodyFocus}
                            onBlur={onBodyBlur}
                            maxLength={maxLength}
                            disabled={user === null}
                            placeholder="Votre commentaire..."
                            className={styles.bodyField}
                        />
                        <div className={styles.toolbar}>
                            <div className={styles.characterCount}>
                                {fields.body.value?.length || 0} / {maxLength}
                            </div>
                            <FormButton
                                type="submit"
                                disabled={user === null}
                                className={classNames([styles.smallButton, styles.submitButton])}
                            >
                                {parent !== null ? 'Répondre' : 'Envoyer'}
                            </FormButton>
                        </div>
                    </FormControl>
                </form>
            )}
        </div>
    );
}

export default CommentForm;
